import { fetchUserTree } from './userTreeService';

// Cache variables
let cachedSelfData = null;
let cacheTimestamp = null;
const CACHE_DURATION_MS = 5 * 60000; // 5 * 60 seconds cache

// Request lock mechanism
let fetchPromise = null;
let isRequestInProgress = false;
let requestCount = 0;

// Cache for agent chain
let cachedAgentChain = null;

// Helper function to get hierarchy level
const getHierarchyLevel = (type) => {
  const levels = {
    'admin': 6,
    'country': 5,
    'city': 4,
    'super-agent': 3,
    'agent': 2,
    'player': 1
  };
  return levels[type] || 0;
};

export const fetchSelf = async (forceRefresh = false) => {
  // Increment request counter to track concurrent calls
  requestCount++;
  const currentRequestId = requestCount;
  console.log(`👤 [${currentRequestId}] Self request started`);
  
  try {
    // Check if we have valid cached data and not forcing refresh
    const now = Date.now();
    if (!forceRefresh && cachedSelfData && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION_MS)) {
      console.log(`👤 [${currentRequestId}] Using cached self data`, { cacheAge: now - cacheTimestamp + "ms" });
      return cachedSelfData;
    }

    // If there's already a request in progress, wait for it to complete instead of making a new one
    if (isRequestInProgress && fetchPromise) {
      console.log(`👤 [${currentRequestId}] Request already in progress, waiting for it to complete...`);
      return fetchPromise;
    }

    // Set lock and create a new promise
    isRequestInProgress = true;
    console.log(`👤 [${currentRequestId}] Fetching fresh self data`);
    
    fetchPromise = (async () => {
      try {
        // Get the user tree and extract the self node
        const treeData = await fetchUserTree(forceRefresh, {
          staleWhileRevalidate: !forceRefresh
        });
        const selfData = treeData.main || treeData; // Handle both main node and direct tree data
        
        // Update cache
        cachedSelfData = selfData;
        cacheTimestamp = Date.now();
        
        console.log(`👤 [${currentRequestId}] Fresh self data fetched and cached`, selfData);
        return selfData;
      } finally {
        // Release lock
        isRequestInProgress = false;
      }
    })();
    
    return fetchPromise;
  } catch (error) {
    console.error(`👤 [${currentRequestId}] Error fetching self data:`, error);
    // If there's an error but we have cached data, return it as a fallback
    if (cachedSelfData) {
      console.log(`👤 [${currentRequestId}] Returning cached data after fetch error`);
      return cachedSelfData;
    }
    throw error;
  } finally {
    console.log(`👤 [${currentRequestId}] Self request completed`);
  }
};

// Helper methods to get specific user data
export const getSelfUsername = async () => {
  const selfData = await fetchSelf();
  return selfData?.username || selfData?.name; // Try both username and name fields
};

export const getSelfUserType = async () => {
  const selfData = await fetchSelf();
  return selfData?.type || selfData?.userType; // Try both type and userType fields
};

// Force refresh the cache
export const refreshSelfData = async () => {
  return fetchSelf(true);
};

// Clear cache function for logout
export const clearCache = () => {
  console.log('🧹 Clearing userSelf cache');
  cachedSelfData = null;
  cacheTimestamp = null;
  cachedAgentChain = null;
  
  // Clear any pending requests
  isRequestInProgress = false;
  fetchPromise = null;
  
  console.log('🧹 UserSelf cache cleared');
};

/**
 * Get the chain of agents above the current user in the hierarchy
 * Returns an array of agent objects in descending order of hierarchy (admin -> current user's parent)
 * @returns {Promise<Array<{username: string, name: string, type: string}>>} Array of agent objects
 */
export const getAgentChain = async (forceRefresh = false) => {
  // Use cached agent chain if available and not forcing refresh
  if (!forceRefresh && cachedAgentChain) {
    return cachedAgentChain;
  }

  try {
    // Get the full tree data which includes the pre-calculated agentChain
    const treeData = await fetchUserTree(forceRefresh, {
      staleWhileRevalidate: !forceRefresh
    });
    const selfData = treeData.main || treeData;
    
    // Get the agent chain from the response
    cachedAgentChain = selfData.agentChain || [];
    
    return cachedAgentChain;
  } catch (error) {
    console.error('Error getting agent chain:', error);
    // Return cached chain if available as fallback
    if (cachedAgentChain) {
      return cachedAgentChain;
    }
    throw error;
  }
};

/**
 * Get the full chain for a target user, including all agents from their level up through the self chain
 * @param {string} username - Username of the target user
 * @returns {Promise<Array<string>>} Array of usernames in the chain, sorted from admin down to target user
 */
export const getFullChain = async (username) => {
  try {
    const userTree = await fetchUserTree(false, { staleWhileRevalidate: true });
    const selfData = userTree.main || userTree;
    if (!selfData) {
      throw new Error('Could not get self data');
    }
    const selfAgentChain = selfData.agentChain || [];
    cachedAgentChain = selfAgentChain;

    // Find a user node by username in the tree
    const findUserNode = (node, searchUsername) => {
      if (!node) return null;
      if (node.username === searchUsername) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = findUserNode(child, searchUsername);
          if (found) return found;
        }
      }
      return null;
    };

    // Find the target user's node
    const userNode = findUserNode(userTree, username);
    
    if (!userNode) {
      console.error('Target user not found in tree:', username);
      return [username];
    }

    // Function to find path from a node to target user
    const findPathToTarget = (node, targetUsername, path = []) => {
      if (!node) return null;
      
      // Found the target
      if (node.username === targetUsername) {
        return [...path, node];
      }
      
      // Check children
      if (node.children) {
        for (const child of node.children) {
          const newPath = findPathToTarget(child, targetUsername, [...path, node]);
          if (newPath) return newPath;
        }
      }
      
      return null;
    };

    // Find path from self to target user (now returning nodes instead of just usernames)
    const pathFromSelf = findPathToTarget(selfData, username, []);

    // Collect all unique usernames with their types
    const usersWithTypes = new Map();

    // Add target user
    usersWithTypes.set(username, {
      username,
      type: userNode.type,
      level: getHierarchyLevel(userNode.type)
    });

    // Add users from path (excluding target)
    if (pathFromSelf) {
      // pathFromSelf now contains full nodes, not just usernames
      const pathWithoutTarget = pathFromSelf.slice(0, -1);
      for (const node of pathWithoutTarget) {
        usersWithTypes.set(node.username, {
          username: node.username,
          type: node.type,
          level: getHierarchyLevel(node.type)
        });
      }
    }

    // Add users from self's agent chain
    if (selfAgentChain && selfAgentChain.length > 0) {
      for (const agent of selfAgentChain) {
        usersWithTypes.set(agent.username, {
          username: agent.username,
          type: agent.type,
          level: getHierarchyLevel(agent.type)
        });
      }
    }

    // Convert to array and sort by hierarchy level (highest to lowest)
    const sortedChain = Array.from(usersWithTypes.values())
      .sort((a, b) => {
        // First sort by level (highest to lowest)
        const levelDiff = b.level - a.level;
        if (levelDiff !== 0) return levelDiff;
        
        // If same level, maintain the order from the chain
        const aInChain = selfAgentChain.findIndex(agent => agent.username === a.username);
        const bInChain = selfAgentChain.findIndex(agent => agent.username === b.username);
        
        // If both in chain, maintain chain order
        if (aInChain !== -1 && bInChain !== -1) {
          return aInChain - bInChain;
        }
        
        // If only one in chain, chain members come first
        if (aInChain !== -1) return -1;
        if (bInChain !== -1) return 1;
        
        // Otherwise, maintain the order from the path
        return 0;
      })
      .map(user => user.username);

    console.log('Sorted chain with types:', Array.from(usersWithTypes.values()).map(user => 
      `${user.username} (${user.type}, level: ${user.level})`
    ));
    console.log('Final sorted chain:', sortedChain);

    return sortedChain;
  } catch (error) {
    console.error('Error getting full chain:', error);
    // Return at least the target user if there's an error
    return [username];
  }
};

/**
 * Get list of usernames that directly report to the current user
 * @returns {Promise<Array<string>>} Array of usernames that are direct reports
 */
export const getDirectReports = async () => {
  try {
    // Get the user tree data
    const treeData = await fetchUserTree(false, { staleWhileRevalidate: true });
    const selfData = treeData.main || treeData;

    // If no self data or no children, return empty array
    if (!selfData || !selfData.children) {
      return [];
    }

    // Extract usernames of direct reports
    const directReports = selfData.children.map(child => child.username);
    console.log('Direct reports found:', directReports);
    
    return directReports;
  } catch (error) {
    console.error('Error getting direct reports:', error);
    return [];
  }
}; 